<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header Section -->
    <TopNavbar />

    <!-- Contact Selection -->
    <div class="p-4 flex max-w-4xl mx-auto w-full flex-col">
      <!-- Title -->
      <div class="py-2 flex justify-between items-center w-full">
        <button @click="goBack" class="text-prime-golden text-2xl">
          <i class="fa fa-chevron-left"></i>
        </button>
        <h2 class="text-2xl flex-1 font-semibold">Add Contact to {{ projectName }}</h2>
      </div>

      <!-- Searchable Dropdown for Existing Contacts -->
      <div class="my-4">
        <!--<div class="mb-4 flex justify-start w-full">
          <button
            @click="createNewContact"
            class="bg-prime-golden text-pitch-black py-2 px-4 rounded-lg"
          >
            + New Contact
          </button>
        </div>-->

        <!-- Tags for Selected Contacts -->
        <!--<div class="flex flex-wrap gap-2 mb-4">
          <div
            v-for="contact in selectedContacts"
            :key="contact.id"
            class="flex items-center bg-light-grey-translucent-500 text-white py-1 px-2 rounded-lg"
          >
            <span>{{ getContactName(contact) }}</span>
            <button
              @click="removeContact(contact)"
              class="ml-2 text-xs text-pitch-black rounded-full "
            >
              <i class="fas fa-times text-ash-grey text-lg"></i>
            </button>
          </div>
        </div>-->

        <!-- Dropdown Input for Selecting Contacts -->
        <div class="flex justify-between items-center gap-4">
          <!--<button v-if="!dropdownOpen" @click="createNewContact">
            <i class="fa fa-plus text-prime-golden text-2xl"></i>
          </button>-->
          <div
            :class="[
              'flex flex-1 relative rounded-md items-center border border-dark-grey gap-1',
              dropdownOpen ? 'bg-dark-grey py-4' : 'bg-eerie-black py-2',
            ]"
          >
            <div class="w-full relative bg-transparent px-2 flex items-center">
              <!-- When dropdown is closed, show placeholder -->

              <div
                v-if="!dropdownOpen"
                @click="toggleDropdown"
                class="w-full flex justify-between items-center cursor-pointer"
              >
                <span class="text-sm font-semibold text-total-white p-1">
                  {{
                    selectedContacts.length > 0
                      ? selectedContacts[0].first_name +
                        " " +
                        selectedContacts[0].last_name
                      : "Select a contact"
                  }}
                </span>
                <i class="fas fa-chevron-down text-prime-golden mr-1"></i>
              </div>

              <!-- When dropdown is open, show search input -->
              <div v-else class="w-full flex items-center px-2">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search contacts..."
                  class="w-full bg-transparent border-none text-total-white outline-none"
                />
                <button
                  @click="closeDropdown"
                  class="text-xs text-pitch-black px-1 rounded-full bg-ash-grey ml-2"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <!-- Dropdown List -->
              <div
                v-if="dropdownOpen"
                class="absolute top-10 mt-1 w-full left-0 bg-eerie-black border border-dark-grey rounded-md z-10"
              >
                <!-- No contacts available to add message -->
                <li
                  v-if="filteredContacts.length === 0"
                  @mousedown.prevent="createNewContact"
                  class="p-2 hover:bg-dark-grey cursor-pointer flex items-center"
                >
                  <span class="text-prime-golden">+ New Contact</span>
                </li>
                <!-- Contact List -->
                <ul v-else>
                  <li
                    @mousedown.prevent="createNewContact"
                    class="p-2 hover:bg-dark-grey cursor-pointer flex items-center"
                  >
                    <span class="text-prime-golden">+ New Contact</span>
                  </li>
                  <li
                    v-for="contact in filteredContacts"
                    :key="contact.id"
                    @mousedown.prevent="selectContact(contact)"
                    class="p-2 hover:bg-dark-grey cursor-pointer flex items-center"
                  >
                    <div
                      class="w-8 h-8 rounded-full flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark overflow-hidden mr-2"
                    >
                      <template v-if="contact.profile_image_url">
                        <img
                          :src="contact.profile_image_url"
                          alt="Profile Image"
                          class="w-full h-full rounded-full border"
                        />
                      </template>
                      <template v-else>
                        <i class="fa-solid fa-user text-2xl text-pitch-black"></i>
                      </template>
                    </div>
                    <span>{{ getContactName(contact) }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Contacts Button -->
    <div class="p-4 max-w-4xl mx-auto mb-4">
      <button
        @click="addContactsToProject"
        :disabled="selectedContacts.length === 0"
        class="w-full py-2 px-4 rounded-lg text-sm font-semibold bg-prime-golden text-pitch-black disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Add Contact to Project
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useProjectStore } from "@/stores/project";
import { useContactsStore } from "@/stores/contact";
import TopNavbar from "@/components/TopNavbar.vue";
import { customFilterAndSort } from "@/misc/util";

const router = useRouter();
const route = useRoute();
const projectStore = useProjectStore();
const contactsStore = useContactsStore();
const projectContacts = ref([]);
const contacts = ref([]);
const projectId = route.params.id;
const searchQuery = ref("");
const dropdownOpen = ref(false);
const selectedContacts = ref([]);
const projectName = computed(
  () => projectStore?.currentProject?.address_1 || "Untitled Project"
);

const fetchContactsofProject = async () => {
  try {
    await contactsStore.fetchContactsByProjectId(projectId);
    projectContacts.value = contactsStore.contacts;
  } catch (err) {
    return err;
  }
};

const fetchAllContacts = async () => {
  try {
    await contactsStore.fetchAllContacts();
    contacts.value = contactsStore.contacts;
  } catch (err) {
    return err;
  }
};
onMounted(async () => {
  await projectStore.fetchProjectById(projectId);
  await fetchAllContacts(); // Fetch contacts on mount
  await fetchContactsofProject();

  console.log(
    projectStore.projectWithPendingStakeholdersToAdd,
    projectStore.stakeholdersToAdd
  );

  if (
    projectStore.projectWithPendingStakeholdersToAdd === projectId &&
    projectStore.stakeholdersToAdd.length > 0
  ) {
    selectedContacts.value = projectStore.stakeholdersToAdd;
  }
});
const filteredContacts = computed(() => {
  const existingContactIds = projectContacts.value?.map((c) => c.id) || [];

  // First, filter out contacts already in the project (or selected)
  // Then map each contact to { label, value, contact }
  const validContacts = contacts.value
    .filter(
      (contact) =>
        !existingContactIds.includes(contact.id) &&
        !selectedContacts.value.some((sel) => sel.id === contact.id)
    )
    .map((contact) => ({
      label: `${contact.first_name} ${contact.last_name}`, // used for sorting
      value: contact.id,
      original: contact, // keep a reference to the original contact object
    }));

  // Now apply customFilterAndSort to get the correct ordering
  const sorted = customFilterAndSort(validContacts, searchQuery.value);

  // If you still want to show only matches (like your old “includes” logic),
  // your customFilterAndSort is already filtering items whose label
  // includes the query. So you're good.

  // Finally, map back to the original contact objects if desired
  return sorted.map((item) => item.original);
});

const toggleDropdown = () => {
  dropdownOpen.value = true;
  searchQuery.value = "";
};

const selectContact = (contact) => {
  //selectedContacts.value.push(contact);
  selectedContacts.value = [contact];
  dropdownOpen.value = false;
  searchQuery.value = "";
};

const removeContact = (contact) => {
  selectedContacts.value = selectedContacts.value.filter((c) => c.id !== contact.id);
};

const closeDropdown = () => {
  dropdownOpen.value = false;
  searchQuery.value = "";
};

const getContactName = (contact) => {
  return `${contact.first_name} ${contact.last_name}`;
};

const createNewContact = () => {
  projectStore.setStakeholdersToAdd(selectedContacts.value, projectId);
  router.push({
    name: "CreateContactPage",
    params: { projectId: projectId },
    query: { from_project_add_contact: projectId },
  });
};

const addContactsToProject = async () => {
  if (selectedContacts.value.length > 0) {
    const stakeholderIds = selectedContacts.value.map((contact) => contact.id);
    try {
      // Call the API to add stakeholders to the project
      await contactsStore.addStakeholdersToProject(projectId, stakeholderIds);

      // Clear the selected contacts after successful addition
      selectedContacts.value = [];

      // Navigate back to the Project page with the Contacts tab open
      router.push({
        name: "ProjectPage",
        params: { id: projectId },
        query: { tab: "contacts" },
      });
    } catch (error) {
      console.error("Error adding contacts to project:", error);
    }
  }
};

const goBack = () => {
  projectStore.setStakeholdersToAdd(selectedContacts.value, projectId);
  router.push({
    name: "ProjectPage",
    params: { id: projectId },
    query: { tab: "contacts" },
  });
};
</script>

<style scoped>
/* You can add your custom styles here to improve the UI/UX */
</style>
